import { CommonModule, Location } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { IonicModule, ModalController, NavController } from '@ionic/angular';
import { ColumnMode, NgxDatatableModule, SelectionType } from '@swimlane/ngx-datatable';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';

import { genericCompareFn } from 'src/app/utils/utils';
import { ListRequest } from 'src/models';
import { USER_GROUP_SOURCE_LABELS, UserGroup, UserGroupSourceType } from 'src/models/user-groups';
import { SharedPipesModule } from 'src/pipes/shared-pipes.module';
import { AuthService } from 'src/services/auth.service';
import { MessageService } from 'src/services/message.service';
import { UserGroupsService } from 'src/services/user-groups.service';

import { EditUserGroupComponent } from '../edit-user-group/edit-user-group.component';

@Component({
  standalone: true,
  imports: [CommonModule, FormsModule, IonicModule, NgxDatatableModule, SharedPipesModule],
  selector: 'app-user-groups-list',
  templateUrl: './user-groups-list.component.html',
  styleUrls: ['./user-groups-list.component.scss'],
})
export class UserGroupsListComponent implements OnInit, OnDestroy {
  userGroups: Partial<UserGroup>[] = [];

  searchText: string;
  isLoading = false;
  ColumnMode = ColumnMode;
  SelectionType = SelectionType;
  pageCount = 1;
  pageSize = 50;
  routerSub: Subscription;
  filteringByProject = true;
  canCreateUserGroup = false;
  isReadOnly = true;

  id?: string;

  constructor(
    public authService: AuthService,
    private modalCtrl: ModalController,
    private navCtrl: NavController,
    private userGroupsService: UserGroupsService,
    private msgSrvc: MessageService,
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
  ) {
    this.id = this.route.snapshot.queryParams.id;
  }

  async ngOnInit() {
    const currentPath = this.location.path();

    this.canCreateUserGroup =
      this.authService.isTenantAdmin ||
      (this.authService.selectedProject?.id && this.authService.isProjectOwner(this.authService.selectedProject?.id));
    this.filteringByProject = !!this.authService.selectedProject?.id;

    this.routerSub = this.router.events
      .pipe(filter((e) => e instanceof NavigationEnd && currentPath === e.url))
      .subscribe(async () => {
        this.isLoading = true;

        await Promise.all([this.getUserGroups()]);

        this.isLoading = false;
      });

    if (this.id) {
      this.openEditUserGroupModal(this.id);
    }
  }

  ngOnDestroy() {
    this.routerSub.unsubscribe();
  }

  editUserGroup(id: string) {
    this.navCtrl.navigateForward(this.router.url, {
      queryParams: {
        id,
      },
    });

    this.openEditUserGroupModal(id);
  }

  async openEditUserGroupModal(id: string) {
    if (!this.canCreateUserGroup) {
      this.msgSrvc.showError('You do not have permission to edit this user group');
      return;
    }

    const modal = await this.modalCtrl.create({
      component: EditUserGroupComponent,
      cssClass: 'modal-tall',
      componentProps: {
        id,
      },
    });

    modal.present();

    modal.onDidDismiss().then(() => {
      this.getUserGroups();

      this.id = undefined;

      this.navCtrl.navigateForward(this.router.url, {
        queryParams: {},
      });
    });
  }

  async getUserGroups() {
    try {
      const options: ListRequest = {
        limit: null,
        order: ['name'],
        descending: false,
        where: [],
      };

      // Filter by project: if project selected, show that project's groups
      // If "All Projects", show only tenant-level groups (projectId = null)
      if (this.filteringByProject) {
        options.where.push({
          field: 'projectId',
          operator: '==',
          value: this.authService.selectedProject?.id,
        });
      }

      if (this.searchText?.length) {
        options.where.push({
          field: 'name',
          operator: 'like',
          value: `%${this.searchText}%`,
        });
      }

      const userGroupsResponse = await this.userGroupsService.list(options);

      this.userGroups = userGroupsResponse.data;
    } catch (err) {
      this.msgSrvc.showError(err);
    }
  }

  showNextPage() {
    this.pageCount += 1;
  }

  async createUserGroup() {
    const modal = await this.modalCtrl.create({
      component: EditUserGroupComponent,
      cssClass: 'modal-tall',
      componentProps: {
        projectId: this.authService.selectedProject?.id ?? null,
      },
    });

    modal.present();

    modal.onDidDismiss().then((data) => {
      this.getUserGroups();
    });
  }

  getSourceLabel(sourceType: UserGroupSourceType) {
    return USER_GROUP_SOURCE_LABELS[sourceType] ?? 'Custom';
  }

  compareWith(v1, v2) {
    return genericCompareFn(v1, v2);
  }
}
